// src/pages/ReOrderCategory.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";

/* ===== axios instance + auth ===== */
const API_BASE = "https://laravel-api.aj-mart.com";

function readAuth() {
  try {
    const raw = localStorage.getItem("auth");
    if (!raw) return { token: null };
    const obj = JSON.parse(raw);
    return { token: obj?.token || null };
  } catch {
    return { token: null };
  }
}

const http = axios.create({
  baseURL: API_BASE,
  timeout: 15000,
  withCredentials: true,
  headers: { Accept: "application/json" },
});

http.interceptors.request.use((config) => {
  const { token } = readAuth();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const LIST_URL = "/api/categories";
const REORDER_URL = "/api/categories/reorder";

export default function ReOrderCategory() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  // DnD state
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);

  // ترتيب أصلي (based on stable keys)
  const originalOrderRef = useRef([]);

  // مفتاح فريد ثابت حتى لو فيه IDs مكررة
  const attachStableKeys = (list) => {
    const counts = {};
    return list.map((c) => {
      const rawId = c?.id ?? c?.category_id ?? "noid";
      counts[rawId] = (counts[rawId] || 0) + 1;
      return { ...c, __key: `${rawId}#${counts[rawId]}` };
    });
  };

  const fetchCategories = async () => {
    setLoading(true);
    setError("");
    setNotice("");
    try {
      const res = await http.get(LIST_URL);
      const list = res.data?.category || res.data?.categories || res.data || [];
      const sorted = [...list].sort((a, b) => {
        const ao = Number(a.sort_order ?? 0);
        const bo = Number(b.sort_order ?? 0);
        if (ao !== bo) return ao - bo;
        return Number(a.id ?? a.category_id ?? 0) - Number(b.id ?? b.category_id ?? 0);
      });
      const withKeys = attachStableKeys(sorted);
      setItems(withKeys);
      originalOrderRef.current = withKeys.map((c) => c.__key);
    } catch (e) {
      setError(e?.response?.data?.message || "Error fetching categories.");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCategories(); }, []);

  const hasChanges = useMemo(() => {
    const curr = items.map((c) => c.__key).join(",");
    const orig = originalOrderRef.current.join(",");
    return curr !== orig;
  }, [items]);

  // إعادة ترتيب حسب index
  const reorder = (fromIndex, toIndex) => {
    if (
      fromIndex == null ||
      toIndex == null ||
      fromIndex === toIndex ||
      fromIndex < 0 ||
      toIndex < 0 ||
      fromIndex >= items.length ||
      toIndex >= items.length
    ) return;

    const next = [...items];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    setItems(next);
  };

  // DnD
  const onDragStart = (e, idx) => {
    setDragIndex(idx);
    try {
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/plain", String(idx));
    } catch {}
  };
  const onDragOver = (e, idx) => {
    e.preventDefault();
    try { e.dataTransfer.dropEffect = "move"; } catch {}
    setOverIndex(idx);
  };
  const onDrop = (e, idx) => {
    e.preventDefault();
    let from = dragIndex;
    try {
      const dataIdx = e.dataTransfer.getData("text/plain");
      if (dataIdx !== "" && !Number.isNaN(Number(dataIdx))) from = Number(dataIdx);
    } catch {}
    reorder(from, idx);
    setDragIndex(null);
    setOverIndex(null);
  };
  const onDragEnd = () => { setDragIndex(null); setOverIndex(null); };

  const moveUp = (idx) => reorder(idx, idx - 1);
  const moveDown = (idx) => reorder(idx, idx + 1);

  const resetLocal = () => {
    const orderKeys = originalOrderRef.current;
    const map = new Map(items.map((c) => [c.__key, c]));
    setItems(orderKeys.map((k) => map.get(k)).filter(Boolean));
    setError("");
    setNotice("");
  };

  const saveOrder = async () => {
    setSaving(true);
    setError("");
    setNotice("");

    const payload = {
      order: items.map((c, i) => ({
        id: Number(c.id ?? c.category_id),
        sort_order: i + 1,
      })),
    };

    try {
      await http.post(REORDER_URL, payload);
    } catch (e1) {
      try {
        await http.put(REORDER_URL, payload);
      } catch (e2) {
        const msg =
          e2?.response?.data?.message ||
          e1?.response?.data?.message ||
          e2?.message ||
          e1?.message ||
          "Failed to save order";
        setError(msg);
        setSaving(false);
        return;
      }
    }

    try {
      const beforeIds = items.map((c) => Number(c.id ?? c.category_id)).join(",");
      await fetchCategories();
      const afterIds = items.map((c) => Number(c.id ?? c.category_id)).join(",");
      if (beforeIds === afterIds) {
        setNotice("Order saved successfully.");
      } else {
        setError("Server did not persist the new order. Check auth/IDs on server.");
      }
    } catch {
      setNotice("Order saved, refresh to see changes.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen px-3 sm:px-6 lg:px-8 py-6 sm:py-8">
      <div className="mx-auto max-w-5xl">
        <div className="flex flex-col gap-2 sm:gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h1 className="text-lg sm:text-2xl font-bold">Reorder Categories (Drag & Drop)</h1>
          <div className="flex flex-wrap gap-2 w-full sm:w-auto">
            <button
              onClick={resetLocal}
              className="rounded-xl border px-4 py-2 text-sm bg-white hover:bg-slate-50 disabled:opacity-60 w-full sm:w-auto"
              disabled={!hasChanges}
            >
              Revert
            </button>
            <button
              onClick={fetchCategories}
              className="rounded-xl border px-4 py-2 text-sm bg-white hover:bg-slate-50 w-full sm:w-auto"
            >
              Refresh
            </button>
            <button
              onClick={saveOrder}
              className={`rounded-xl px-4 py-2 text-sm text-white w-full sm:w-auto ${
                saving ? "bg-indigo-300" : "bg-indigo-600 hover:bg-indigo-500"
              }`}
              disabled={saving || !items.length}
            >
              {saving ? "Saving…" : "Save Order"}
            </button>
          </div>
        </div>

        {error && (
          <div className="mt-3 sm:mt-4 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-rose-700 text-sm">
            {error}
          </div>
        )}
        {notice && (
          <div className="mt-3 sm:mt-4 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-emerald-700 text-sm">
            {notice}
          </div>
        )}

        <div className="mt-4 sm:mt-6 rounded-2xl border bg-white shadow-sm">
          <div className="p-2 sm:p-4 border-b bg-slate-50/70 rounded-t-2xl text-[12px] sm:text-sm text-slate-600">
            Drag items (or use ↑/↓). No validation until you click <b>Save</b>.
          </div>

          <div className="p-2 sm:p-3">
            {loading ? (
              <div className="grid gap-2">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="h-12 rounded-xl border bg-white flex items-center gap-3 px-3 animate-pulse">
                    <div className="h-4 w-4 bg-slate-200 rounded" />
                    <div className="h-4 w-8 bg-slate-200 rounded" />
                    <div className="h-4 w-64 bg-slate-200 rounded" />
                  </div>
                ))}
              </div>
            ) : items.length ? (
              <ul className="grid gap-2">
                {items.map((c, idx) => (
                  <li
                    key={c.__key}
                    draggable
                    onDragStart={(e) => onDragStart(e, idx)}
                    onDragOver={(e) => onDragOver(e, idx)}
                    onDrop={(e) => onDrop(e, idx)}
                    onDragEnd={onDragEnd}
                    className={[
                      // على الموبايل: كارد مكدّس — على الشاشات الأكبر: صف أفقي
                      "grid grid-cols-1 sm:flex sm:items-center sm:justify-between",
                      "gap-2 sm:gap-3 rounded-xl border bg-white px-3 py-2 sm:px-3 sm:py-2 transition",
                      overIndex === idx ? "ring-2 ring-indigo-200 border-indigo-300" : "hover:shadow-sm",
                    ].join(" ")}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span
                        className="cursor-grab select-none rounded-md border px-2 py-1 text-xs text-slate-600 bg-slate-50"
                        title="Drag handle"
                      >
                        ≡
                      </span>
                      <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-md bg-indigo-50 text-indigo-700 text-xs px-2">
                        {idx + 1}
                      </span>
                      <div className="min-w-0">
                        <div className="text-sm sm:text-base font-semibold text-slate-900 truncate">
                          {c.category_name_E || "-"}
                        </div>
                        <div className="text-xs text-slate-500 truncate">{c.category_name_A || "-"}</div>
                      </div>
                    </div>

                    <div className="flex items-center justify-end sm:justify-end gap-2">
                      <button
                        onClick={() => moveUp(idx)}
                        className="rounded-lg border px-3 py-2 text-sm hover:bg-slate-50 disabled:opacity-40 active:scale-[0.98]"
                        title="Move up"
                        disabled={idx === 0}
                      >
                        ↑
                      </button>
                      <button
                        onClick={() => moveDown(idx)}
                        className="rounded-lg border px-3 py-2 text-sm hover:bg-slate-50 disabled:opacity-40 active:scale-[0.98]"
                        title="Move down"
                        disabled={idx === items.length - 1}
                      >
                        ↓
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="rounded-xl border bg-slate-50 p-6 text-sm text-slate-500">No categories found.</div>
            )}
          </div>

          <div className="px-3 sm:px-4 py-2 sm:py-3 border-t text-[11px] sm:text-xs text-slate-500 rounded-b-2xl flex items-center justify-between">
            <div>{items.length} item(s)</div>
            <div>{hasChanges ? <span className="text-indigo-600 font-medium">unsaved changes</span> : "no changes"}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
